import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Clock, ShieldCheck } from 'lucide-react';
import { BillerBayLogo } from '@/components/ui/BillerBayLogo';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { useLogin, useMfaValidate } from '@/api/auth';
import { useAuth } from '@/hooks/useAuth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { apiError } from '@/lib/api';

const schema = z.object({
  email: z.string().email('Enter a valid email address.'),
  password: z.string().min(1, 'Password is required.'),
});
type FormValues = z.infer<typeof schema>;

function safeRedirect(raw: string | null, fallback: string): string {
  if (!raw) return fallback;
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith('/') && !decoded.startsWith('//')) return decoded;
  } catch { /* fall through */ }
  return fallback;
}

export function SessionExpiredPage() {
  usePageTitle('Session expired');
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const navigate = useNavigate();
  const login = useLogin();
  const mfaValidate = useMfaValidate();
  const { setSession } = useAuth();
  const [serverError, setServerError] = useState<string | null>(null);
  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormValues>({
    resolver: async (values) => {
      const parsed = schema.safeParse(values);
      if (parsed.success) return { values: parsed.data, errors: {} };
      const fieldErrors: Record<string, { type: string; message: string }> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path.join('.');
        if (!fieldErrors[key]) fieldErrors[key] = { type: 'validation', message: issue.message };
      }
      return { values: {}, errors: fieldErrors };
    },
  });

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      const resp = await login.mutateAsync({ ...values, remember_me: rememberMe });
      if (resp.mfa_setup_required && resp.setup_token) {
        sessionStorage.setItem('mfa_setup_token', resp.setup_token);
        navigate('/mfa-setup-required');
        return;
      }
      if (resp.mfa_required && resp.mfa_challenge_token) {
        setMfaChallenge(resp.mfa_challenge_token);
        return;
      }
      setSession(resp);
      navigate(safeRedirect(from, resp.user?.role === 'client_admin' || resp.user?.role === 'client_user' ? '/client' : '/staff'), { replace: true });
    } catch (e) {
      setServerError(apiError(e));
    }
  };

  const onMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const resp = await mfaValidate.mutateAsync({ mfa_challenge_token: mfaChallenge!, code: mfaCode });
      setSession(resp);
      navigate(safeRedirect(from, resp.user?.role === 'client_admin' || resp.user?.role === 'client_user' ? '/client' : '/staff'), { replace: true });
    } catch {
      setServerError('Invalid code. Check your authenticator app and try again.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F2F2F8] dark:bg-[#0D0D1A] p-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-6">
          <BillerBayLogo variant="dark" height={26} />
        </div>

        {/* Expired notice */}
        <div className="flex items-center gap-3 bg-[#FFFBEB] dark:bg-[#2A1F00] border border-[#FDE68A] dark:border-[#5C4400] rounded-xl px-4 py-3 mb-5">
          <Clock size={18} className="text-[#B45309] shrink-0" />
          <div>
            <p className="text-sm font-semibold text-[#B45309]">Session expired</p>
            <p className="text-xs text-[#92400E] dark:text-[#D97706] mt-0.5">You were signed out due to inactivity. Sign back in to continue.</p>
          </div>
        </div>

        <div className="bg-white dark:bg-[#1A1A2E] border border-[#E3E3F1] dark:border-[#2A2A4A] rounded-xl shadow-[0_1px_2px_rgba(15,23,42,0.06)] p-6">
          {mfaChallenge ? (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#EFF0FF] dark:bg-[#1E2040] flex items-center justify-center">
                  <ShieldCheck size={16} className="text-[#0410BD] dark:text-[#818CF8]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#12122C] dark:text-white">Two-factor authentication</p>
                  <p className="text-xs text-[#676687] dark:text-[#8888AA]">Enter the 6-digit code from your authenticator app.</p>
                </div>
              </div>
              <form onSubmit={onMfaSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="mfa-code">Authenticator code</Label>
                  <Input
                    id="mfa-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    autoComplete="one-time-code"
                    autoFocus
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="text-center text-xl tracking-[0.5em] font-mono"
                  />
                </div>
                {serverError && (
                  <div className="text-sm text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-4 py-3">{serverError}</div>
                )}
                <Button type="submit" variant="primary" loading={mfaValidate.isPending} className="w-full justify-center py-2.5">
                  Verify
                </Button>
                <button type="button" onClick={() => { setMfaChallenge(null); setServerError(null); }} className="w-full text-center text-sm text-[#676687] dark:text-[#8888AA] hover:text-[#12122C] dark:hover:text-white">
                  ← Back
                </button>
              </form>
            </div>
          ) : (
            <>
              <h2 className="text-base font-semibold text-[#12122C] dark:text-white mb-4">Sign back in</h2>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
                <div>
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    invalid={!!errors.email}
                    placeholder="name@practice.com"
                    {...register('email')}
                  />
                  {errors.email && <p className="text-xs text-[#B91C1C] mt-1">{errors.email.message}</p>}
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    invalid={!!errors.password}
                    {...register('password')}
                  />
                  {errors.password && <p className="text-xs text-[#B91C1C] mt-1">{errors.password.message}</p>}
                </div>

                {serverError && (
                  <div className="text-sm text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-4 py-3">{serverError}</div>
                )}

                <Button type="submit" variant="primary" loading={isSubmitting || login.isPending} className="w-full justify-center py-2.5 text-sm">
                  {login.isPending ? 'Signing in…' : 'Sign back in'}
                </Button>

                <div className="flex items-center justify-between text-sm pt-1">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 rounded border-[#BABACE] text-[#0410BD] focus:ring-[#3F4CFF]/20"
                    />
                    <span className="text-sm text-[#676687] dark:text-[#8888AA]">Remember me</span>
                  </label>
                  <a href="/login" className="text-[#0410BD] dark:text-[#818CF8] hover:text-[#3F4CFF] text-sm transition-colors">
                    Different account
                  </a>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
